import React from "react";
import {
  Autocomplete,
  FormControl,
  Grid,
  type GridProps,
  InputLabel,
  TextField,
} from "@mui/material";
import type { FormikProps } from "formik";
import { FORM_STYLES, S_GRID_PROPS } from "../../../constants";

export type TerminalWithNameOption = {
  terminalId: string;
  terminalName?: string;
  fax?: number;
  localTelephone?: string;
  terminalAddress1?: string;
  terminalAddress2?: string;
  terminalCity?: string;
  terminalState?: string;
  terminalZip?: string;
  [key: string]: unknown;
};

export interface FormikTerminalAutocompleteProps {
  formik: FormikProps<any>;
  name: string;
  label: string;
  options: TerminalWithNameOption[];
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  gridProps?: GridProps;
  /**
   * Optional mapping to set additional form fields when a terminal is selected.
   * Example: { driverCity: (t) => t.terminalCity }
   */
  setFieldsFromOption?: Record<string, (opt: TerminalWithNameOption) => unknown>;
}

const FormikTerminalAutocomplete: React.FC<FormikTerminalAutocompleteProps> = ({
  formik,
  name,
  label,
  options,
  placeholder,
  required,
  disabled,
  gridProps = S_GRID_PROPS,
  setFieldsFromOption,
}) => {
  const selected = options.find((opt) => opt.terminalId === formik.values?.[name]);

  const touched = Boolean((formik.touched as any)?.[name]);
  const rawError = touched ? (formik.errors as any)?.[name] : undefined;
  const error = Boolean(rawError);

  return (
    <Grid item {...gridProps}>
      <FormControl style={{ minWidth: 120 }} fullWidth size="small">
        <InputLabel style={FORM_STYLES.LABEL} shrink={true} id={`${name}Label`}>
          {label} {required && <sup>*</sup>}
        </InputLabel>
        <Autocomplete<TerminalWithNameOption, false, true, false>
          className="custom-autocomplete"
          noOptionsText="Records not found"
          disablePortal
          forcePopupIcon={false}
          options={options}
          disabled={Boolean(disabled)}
          value={selected}
          disableClearable={true}
          isOptionEqualToValue={(option, value) => option.terminalId === value.terminalId}
          getOptionLabel={(option) => `${option.terminalId} - ${option.terminalName ?? ""}`}
          filterOptions={(allOptions, { inputValue }) =>
            allOptions.filter((option) =>
              (option?.terminalName ?? "")
                .concat(" ", option?.terminalId ?? "")
                .toLowerCase()
                .includes(inputValue?.toLowerCase())
            )
          }
          onChange={(_e, newValue) => {
            formik.setFieldValue(name, newValue?.terminalId ?? "");

            if (newValue && setFieldsFromOption) {
              Object.entries(setFieldsFromOption).forEach(([fieldName, getter]) => {
                formik.setFieldValue(fieldName, getter(newValue));
              });
            }
          }}
          onBlur={() => formik.setFieldTouched(name, true)}
          renderOption={(props, option) => (
            <li
              {...props}
              key={`${option.terminalId?.trim()} - ${option.terminalName?.trim() ?? ""}`}
              title={`${option.terminalId?.trim()} - ${option.terminalName?.trim() ?? ""}`}
            >
              {option.terminalId?.trim()} - {option.terminalName?.trim()}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              autoComplete="off"
              name={name}
              placeholder={placeholder}
              {...params}
              variant="outlined"
              error={error}
              helperText={error ? rawError.toString() : undefined}
            />
          )}
        />
      </FormControl>
    </Grid>
  );
};

export default FormikTerminalAutocomplete;
